/**
 * ActionResultView Component
 *
 * Displays action execution results with success/failure status,
 * entity details, and change summary.
 * Phase 18: Workflow Automation
 *
 * @module ActionResultView
 */

'use client';

import { memo } from 'react';
import { Box, Text, Flex } from '@axon/ui';
import { useTranslations } from 'next-intl';
import { LuCircleCheck, LuCircleX, LuArrowRight } from 'react-icons/lu';

import type { ActionResultData } from '../types/chatbot.types';
import { isEntityId } from '../utils/chatbot.link-utils';

import { EntityLink } from './EntityLink';

// ============================================================================
// Props
// ============================================================================

interface ActionResultViewProps {
  result: ActionResultData;
  message: string;
}

// ============================================================================
// Component
// ============================================================================

export const ActionResultView = memo(function ActionResultView({
  result,
  message,
}: ActionResultViewProps) {
  const t = useTranslations('chatbot');
  const isSuccess = result.success;

  return (
    <Box
      colorPalette={isSuccess ? 'green' : 'red'}
      marginTop="2"
      padding="3"
      bg="colorPalette.subtle"
      borderRadius="md"
      border="1px solid"
      borderColor="colorPalette.emphasized"
    >
      {/* Status header */}
      <Flex align="center" gap="2" marginBottom="1">
        <Box color="colorPalette.fg" flexShrink={0}>
          {isSuccess ? <LuCircleCheck size={16} /> : <LuCircleX size={16} />}
        </Box>
        <Text fontSize="sm" fontWeight="semibold" color="colorPalette.fg">
          {message}
        </Text>
      </Flex>

      {/* Entity info */}
      <Text fontSize="xs" color="fg.muted" marginBottom="1">
        {result.entityType}:{' '}
        {isEntityId(result.entityId) ? (
          <EntityLink entityId={result.entityId} />
        ) : (
          <strong>{result.entityId}</strong>
        )}
      </Text>

      {/* Changes summary */}
      {result.changes && Object.keys(result.changes).length > 0 && (
        <Box marginTop="2" paddingTop="2" borderTop="1px solid" borderColor="colorPalette.emphasized">
          <Text fontSize="xs" color="fg.subtle" marginBottom="1">
            {t('actions.changes')}:
          </Text>
          {/* TODO(batch2): diff value formatting (null/bool/object) instead of raw String() */}
          {Object.entries(result.changes).map(([field, change]) => (
            <Flex key={field} align="center" gap="1" fontSize="xs" color="fg.muted">
              <Text fontWeight="medium">{field}:</Text>
              <Text>{String(change.from)}</Text>
              <LuArrowRight size={10} />
              <Text fontWeight="medium">{String(change.to)}</Text>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
});

export default ActionResultView;
